import axios from 'axios'
import { AUTH_BASE_URL, DATA_BASE_URL, CHECKOUT_BASE_URL } from './env.config'
import store from '@/store/index'

/**
 * =========================
 * Helpers
 * =========================
 */

function getAuthToken() {
    return localStorage.authToken
        ? JSON.parse(localStorage.authToken)
        : null
}

function getRefreshToken() {
    return localStorage.refreshToken
        ? JSON.parse(localStorage.refreshToken)
        : null
}

function setTokens(token, refreshToken) {
    localStorage.authToken = JSON.stringify(token)
    localStorage.refreshToken = JSON.stringify(refreshToken)
}

function logOut() {
    console.log('Logging out user due to authentication failure.')
    store.commit('logOut')
    window.location = '/login/'
}

/**
 * =========================
 * Axios instances
 * =========================
 */

const HTTP = axios.create({
    baseURL: DATA_BASE_URL,
})

const HTTP_AUTH = axios.create({
    baseURL: AUTH_BASE_URL,
})

const CHECKOUT = axios.create({
    baseURL: CHECKOUT_BASE_URL,
})

/**
 * ❗ Axios instance БЕЗ interceptor — тільки для refresh
 */
const REFRESH_HTTP = axios.create({
    baseURL: AUTH_BASE_URL,
})

/**
 * =========================
 * Request interceptors
 * =========================
 */

function attachAuthHeader(config) {
    const token = getAuthToken()
    if (!token) return config

    return {
        ...config,
        headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`,
        },
    }
}

HTTP.interceptors.request.use(attachAuthHeader, Promise.reject)
HTTP_AUTH.interceptors.request.use(attachAuthHeader, Promise.reject)

/**
 * =========================
 * Refresh state
 * =========================
 */

let isRefreshing = false
let refreshPromise = null

/**
 * =========================
 * Response interceptor (401 handler)
 * =========================
 */

async function handle401(error, client) {
    const originalRequest = error.config

    if (!error.response || error.response.status !== 401) {
        return Promise.reject(error)
    }

    // не ретраїмо refresh
    if (originalRequest.url.includes('/auth/refresh')) {
        logOut()
        return Promise.reject(error)
    }

    // вже пробували
    if (originalRequest._retry) {
        logOut()
        return Promise.reject(error)
    }

    const refreshToken = getRefreshToken()
    if (!refreshToken) {
        logOut()
        return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
        if (!isRefreshing) {
            isRefreshing = true
            refreshPromise = REFRESH_HTTP.post('/auth/refresh', {
                refreshToken,
            })
        }

        const { data } = await refreshPromise

        const { token, refreshToken: newRefreshToken } = data.content || {}

        if (!token || !newRefreshToken) {
            throw new Error('Invalid refresh response')
        }

        setTokens(token, newRefreshToken)

        isRefreshing = false
        refreshPromise = null

        return client({
            ...originalRequest,
            headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
            },
        })
    } catch (err) {
        isRefreshing = false
        refreshPromise = null
        logOut()
        return Promise.reject(err)
    }
}

/**
 * =========================
 * Attach response interceptors
 * =========================
 */

HTTP.interceptors.response.use(
    res => res,
    err => handle401(err, HTTP)
)

HTTP_AUTH.interceptors.response.use(
    res => res,
    err => handle401(err, HTTP_AUTH)
)

export {
    HTTP,
    HTTP_AUTH,
    CHECKOUT,
}
